import React from 'react';
import {
  Box,
  Button,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { ArrowRight, CheckCircle, WarningCircle } from 'phosphor-react';

export type Salvamento = 'salvo' | 'pendente' | 'salvando' | 'erro';

interface Props {
  salvamento: Salvamento;
  salvoEm: Date | null;
  onSalvar: () => void;
  pendencias: { papel: string; rotulo: string; grupo: string }[];
  irPara: (papel: string) => void;
  /** A ação principal do momento: ler as fontes ou gerar os documentos. */
  acao: React.ReactNode;
}

/**
 * A barra de ações que acompanha a tela.
 *
 * ⚠️ `position: fixed`, NUNCA `sticky`: nesta aplicação quem rola é o documento,
 * e `sticky` fica inerte em silêncio — a barra só sobe embora (herdado da tela
 * anterior, que aprendeu isso na prática).
 *
 * O status do salvamento é TEXTO sempre visível, não tooltip: a ficha salva
 * sozinha, e o operador precisa ver que salvou antes de sair da tela.
 */
export default function DockContrato({
  salvamento,
  salvoEm,
  onSalvar,
  pendencias,
  irPara,
  acao,
}: Props) {
  const hora = salvoEm?.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  let status: React.ReactNode;
  if (salvamento === 'salvando') {
    status = (
      <Flex align="center" gap={1.5} color="gray.500">
        <Spinner size="xs" />
        <Text fontSize="12px">Salvando…</Text>
      </Flex>
    );
  } else if (salvamento === 'erro') {
    status = (
      <Flex align="center" gap={1.5} color="red.600">
        <WarningCircle size={14} weight="fill" />
        <Text fontSize="12px" fontWeight={600}>
          Não salvou.
        </Text>
        <Button size="xs" variant="link" colorScheme="red" onClick={onSalvar}>
          Tentar de novo
        </Button>
      </Flex>
    );
  } else if (salvamento === 'pendente') {
    status = (
      <Text fontSize="12px" color="gray.500">
        Alterações ainda não salvas…
      </Text>
    );
  } else {
    status = (
      <Flex align="center" gap={1.5} color="green.600">
        <CheckCircle size={14} weight="fill" />
        <Text fontSize="12px">{hora ? `Salvo às ${hora}` : 'Tudo salvo'}</Text>
      </Flex>
    );
  }

  return (
    <Box
      position="fixed"
      bottom={4}
      right={4}
      left={{ base: 4, md: 'auto' }}
      zIndex={20}
      bg="white"
      borderRadius="12px"
      boxShadow="lg"
      border="1px solid"
      borderColor="gray.100"
      px={4}
      py={3}
    >
      <Flex align="center" gap={4} flexWrap="wrap">
        {status}
        {pendencias.length > 0 && (
          <Flex align="center" gap={2}>
            <Popover placement="top" isLazy>
              <PopoverTrigger>
                <Button variant="link" size="sm" color="orange.600" fontWeight={600}>
                  ● {pendencias.length} em branco
                </Button>
              </PopoverTrigger>
              <PopoverContent maxH="320px" overflowY="auto" fontSize="12px" w="auto">
                <PopoverArrow />
                <PopoverBody>
                  {/* O contador diz QUANTO falta; só a lista diz QUAL. */}
                  {pendencias.map(p => (
                    <Text
                      key={p.papel}
                      as="button"
                      type="button"
                      display="block"
                      py="2px"
                      textAlign="left"
                      onClick={() => irPara(p.papel)}
                    >
                      <Text as="span" color="gray.400">
                        {p.grupo} ·{' '}
                      </Text>
                      {p.rotulo}
                    </Text>
                  ))}
                </PopoverBody>
              </PopoverContent>
            </Popover>
            <Button
              size="sm"
              colorScheme="orange"
              leftIcon={<ArrowRight size={14} />}
              onClick={() => irPara(pendencias[0].papel)}
            >
              Próximo em branco
            </Button>
          </Flex>
        )}
        {acao}
      </Flex>
    </Box>
  );
}
